import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  Divider,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

import { isDeadlinePassed } from '../components/SharedComponents';
import useOrderMedia from '../hooks/useOrderMedia';
import usePhotoCaptions from '../hooks/usePhotoCaptions';
import useToast from '../hooks/useToast';
import {
  getBackendPhotoId,
  getPhotoImageUrl,
  isRealBackendPhotoId,
} from '../hooks/useParentPhotos';
import api from '../services/api';

function getSchoolPhotoSelectionId(item) {
  return item?.media_item_id || null;
}

function getSchoolPhotoCandidateIds(item) {
  return [
    ...new Set(
      [
        item?.media_item_id,
        item?.id,
        item?.project_media_id,
      ].filter(Boolean)
    ),
  ];
}

function photoMatchesIdSet(item, idSet) {
  if (!idSet?.size) return false;
  return getSchoolPhotoCandidateIds(item).some((id) => idSet.has(id));
}

function addPhotoAliases(targetSet, item) {
  getSchoolPhotoCandidateIds(item).forEach((id) => targetSet.add(id));
}

function removePhotoAliases(targetSet, item) {
  getSchoolPhotoCandidateIds(item).forEach((id) => targetSet.delete(id));
}

function getSchoolPhotoUrl(item) {
  return item?.display_url || getPhotoImageUrl(item);
}

function isSchoolPhotoSelected(item) {
  return Boolean(
    item?.parent_selected ??
    item?.selected ??
    false
  );
}

function collectSelectedIdsFromSchoolPhotos(data) {
  const explicitSelectedIds = [
    data?.selected_media_item_ids,
    data?.selected_photo_ids,
    data?.selected_ids,
  ].find(Array.isArray);

  if (explicitSelectedIds) {
    return {
      recognized: true,
      ids: new Set(explicitSelectedIds.filter(Boolean)),
    };
  }

  const hasChapterShape = Array.isArray(data) || Array.isArray(data?.chapters);
  const hasDirectPhotoShape = Array.isArray(data?.photos) || Array.isArray(data?.items);

  const chapters = Array.isArray(data)
    ? data
    : Array.isArray(data?.chapters)
      ? data.chapters
      : [];

  const directPhotos = Array.isArray(data?.photos)
    ? data.photos
    : Array.isArray(data?.items)
      ? data.items
      : [];

  const photos = [
    ...directPhotos,
    ...chapters.flatMap((chapter) => [
      ...(Array.isArray(chapter?.photos) ? chapter.photos : []),
      ...(Array.isArray(chapter?.media) ? chapter.media : []),
    ]),
  ];

  return {
    recognized: hasChapterShape || hasDirectPhotoShape,
    ids: new Set(
      photos
        .filter(isSchoolPhotoSelected)
        .flatMap(getSchoolPhotoCandidateIds)
        .filter(Boolean)
    ),
  };
}

export default function MediaGallery({
  orderId,
  projectId,
  limit = 0,
  onUpgrade,
  onCapacityChange,
  bookConfig,
  childName = 'your child',
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const toast = useToast();

  const { mediaList, loading, error } = useOrderMedia(orderId);
  const safeMediaList = useMemo(
    () => (Array.isArray(mediaList) ? mediaList : []),
    [mediaList]
  );

  const [filter, setFilter] = useState('all');
  const [zoomItem, setZoomItem] = useState(null);
  // const [showUpgradePopup, setShowUpgradePopup] = useState(false);
  const [likedIds, setLikedIds] = useState(new Set());
  const [selectionLoading, setSelectionLoading] = useState(false);
  const [updatingSelectionIds, setUpdatingSelectionIds] = useState(new Set());
  const [coverPhotoId, setCoverPhotoId] = useState(null);
  const [isSettingCoverId, setIsSettingCoverId] = useState(null);

  const isViewOnly = useMemo(
    () => isDeadlinePassed(bookConfig?.content_deadline),
    [bookConfig?.content_deadline]
  );

  const captionPhotos = useMemo(
    () =>
      safeMediaList.filter((item) =>
        isRealBackendPhotoId(getBackendPhotoId(item))
      ),
    [safeMediaList]
  );

  const {
    captionsByPhotoId,
    fetchingCaptionIds,
  } = usePhotoCaptions(projectId, captionPhotos, {
    fetchCaptions: false,
    initialCaptionField: 'original_caption',
  });

  useEffect(() => {
    if (!safeMediaList.length) {
      setCoverPhotoId(null);
      return;
    }

    const initialCover = safeMediaList.find((item) => item?.is_cover);
    setCoverPhotoId(initialCover ? getSchoolPhotoSelectionId(initialCover) : null);
  }, [safeMediaList]);

  useEffect(() => {
    // Reset project-scoped UI state only when the project/order changes.
    // Do not reset likedIds when safeMediaList arrives later, otherwise a faster
    // getSchoolPhotos response can be accidentally overwritten.
    setLikedIds(new Set());
  }, [orderId, projectId]);

  useEffect(() => {
    const selectedFromMedia = new Set();
    safeMediaList.filter(isSchoolPhotoSelected).forEach((item) => {
      addPhotoAliases(selectedFromMedia, item);
    });

    if (selectedFromMedia.size > 0) {
      setLikedIds((prev) => new Set([...prev, ...selectedFromMedia]));
    }
  }, [safeMediaList]);

  useEffect(() => {
    if (!projectId) return undefined;

    let cancelled = false;

    const loadSelections = async () => {
      setSelectionLoading(true);
      try {
        const data = await api.getSchoolPhotos(projectId);
        if (cancelled) return;

        const persistedSelections = collectSelectedIdsFromSchoolPhotos(data);
        if (persistedSelections.recognized) {
          // Merge with flags already present on safeMediaList. This keeps the gallery
          // source of truth as safeMediaList while still restoring saved likes.
          setLikedIds((prev) => new Set([...prev, ...persistedSelections.ids]));
        }
      } catch (err) {
        console.error('Failed to load school photo selections:', err);
      } finally {
        if (!cancelled) setSelectionLoading(false);
      }
    };

    loadSelections();

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const numericLimit = Number(limit);
  const hasPhotoLimit = Number.isFinite(numericLimit) && numericLimit >= 0;
  const photoLimit = hasPhotoLimit ? numericLimit : Infinity;

  const likedMediaIds = useMemo(() => {
    const ids = new Set();
    safeMediaList.forEach((item) => {
      if (photoMatchesIdSet(item, likedIds)) {
        const id = getSchoolPhotoSelectionId(item);
        if (id) ids.add(id);
      }
    });
    return ids;
  }, [safeMediaList, likedIds]);

  const likedCount = likedMediaIds.size;
  const atLimit = hasPhotoLimit && likedCount >= photoLimit;
  const availableSlots = hasPhotoLimit
    ? Math.max(photoLimit - likedCount, 0)
    : Infinity;

  // Always derive what is rendered from safeMediaList. Filters only choose which
  // safeMediaList items are visible; they never replace the gallery data source.
  const displayedMedia = useMemo(() => {
    if (filter === 'all') return safeMediaList;

    return safeMediaList.filter((item) => {
      if (filter === 'selected') {
        return photoMatchesIdSet(item, likedIds);
      }

      const faceCount = Number(item?.face_count) || 0;

      let type = 'none';

      if (faceCount === 1) {
        type = 'solo';
      } else if (faceCount >= 2 && faceCount <= 3) {
        type = 'social';
      } else if (faceCount >= 4) {
        type = 'group';
      }

      return type === filter;
    });
  }, [filter, safeMediaList, likedIds]);

  const handleFilterChange = (_event, newFilter) => {
    if (newFilter !== null) setFilter(newFilter);
  };

 const openSelectionUpgrade = (item = null) => {
    if (!onUpgrade) return;

    onUpgrade({
      source: 'school_photos',
      reason: 'school_photo_limit',

      // The parent attempted to add one more photo.
      attempted: item ? 1 : 0,

      // At the limit this should normally be 0.
      remaining: availableSlots,

      currentLimit: hasPhotoLimit ? photoLimit : null,
      usedSlots: likedCount,

      // Optional context for a future auto-select enhancement.
      // Do NOT auto-select it in this first implementation.
      pendingMediaItemId: item
        ? getSchoolPhotoSelectionId(item)
        : null,
    });
  };

  const setSelectionPending = (photoId, pending) => {
    setUpdatingSelectionIds((prev) => {
      const next = new Set(prev);
      if (pending) next.add(photoId);
      else next.delete(photoId);
      return next;
    });
  };

  const toggleLiked = async (item, forceLiked) => {
    if (isViewOnly || !projectId) return;

    const photoId = getSchoolPhotoSelectionId(item);
    if (!photoId || updatingSelectionIds.has(photoId)) return;

    const wasLiked = photoMatchesIdSet(item, likedIds);
    const nextLiked = typeof forceLiked === 'boolean' ? forceLiked : !wasLiked;
    if (nextLiked === wasLiked) return;

    if (nextLiked && atLimit) {
      openSelectionUpgrade(item);
      return;
    }

    setSelectionPending(photoId, true);
    setLikedIds((prev) => {
      const next = new Set(prev);
      if (nextLiked) addPhotoAliases(next, item);
      else removePhotoAliases(next, item);
      return next;
    });
    try {
      await api.updateSchoolPhotoSelections(projectId, {
        select: nextLiked ? [photoId] : [],
        deselect: nextLiked ? [] : [photoId],
      });

      await onCapacityChange?.({
        showLoading: false,
      });

      toast.success(nextLiked ? 'Photo added to the book.' : 'Photo removed from the book.', {
        autoHideDuration: 1500,
        replace: true,
      });
    } catch (err) {
      console.error('Failed to update school photo selection:', err);
      setLikedIds((prev) => {
        const next = new Set(prev);
        if (wasLiked) addPhotoAliases(next, item);
        else removePhotoAliases(next, item);
        return next;
      });
      toast.error('Could not update the photo selection. Please try again.');
    } finally {
      setSelectionPending(photoId, false);
    }
  };

  const handleOpenZoom = (item) => setZoomItem(item);
  const handleCloseZoom = () => setZoomItem(null);

  const handleSetCover = async (item) => {
    const photoId = getSchoolPhotoSelectionId(item);
    if (!photoId) return;

    setIsSettingCoverId(photoId);
    try {
      // Existing gallery behavior retained until a school-photo cover endpoint is defined.
      await new Promise((resolve) => setTimeout(resolve, 500));
      setCoverPhotoId(photoId);
    } catch (err) {
      console.error('Failed to set cover:', err);
    } finally {
      setIsSettingCoverId(null);
    }
  };

  const zoomSelectionId = zoomItem ? getSchoolPhotoSelectionId(zoomItem) : null;
  const zoomCaptionId = zoomItem ? getBackendPhotoId(zoomItem) : null;
  const zoomCaption = zoomCaptionId ? captionsByPhotoId[zoomCaptionId] || '' : '';
  const zoomLiked = zoomItem ? photoMatchesIdSet(zoomItem, likedIds) : false;
  const zoomSelectionPending = zoomSelectionId
    ? updatingSelectionIds.has(zoomSelectionId)
    : false;
  const isZoomItemCover = zoomSelectionId && zoomSelectionId === coverPhotoId;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress size={40} thickness={4} sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" variant="filled" sx={{ borderRadius: '6px' }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 6 }}>
      <Box sx={{ mb: { xs: 3.5, md: 4 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 2.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
              mb: 0.75,
            }}
          >
            <Typography sx={{ fontSize: { xs: 24, sm: 28 }, fontWeight: 850, color: 'text.primary' }}>
              School Photos Gallery
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.25,
                py: 0.6,
                borderRadius: '999px',
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              {selectionLoading && <CircularProgress size={13} />}
              <Typography sx={{ fontSize: 13, fontWeight: 750, color: 'text.primary' }}>
                {hasPhotoLimit
                  ? `${likedCount} of ${photoLimit} selected`
                  : `${likedCount} selected`}
              </Typography>
              {hasPhotoLimit && (
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                  · {Math.max(photoLimit - likedCount, 0)} remaining
                </Typography>
              )}
              {atLimit && onUpgrade && !isViewOnly && (
                <Button
                  size="small"
                  onClick={() => openSelectionUpgrade()}
                  sx={{
                    minWidth: 0,
                    p: 0,
                    ml: 0.25,
                    fontSize: 12,
                    fontWeight: 800,
                    textTransform: 'none',
                  }}
                >
                  Upgrade
                </Button>
              )}
            </Box>
          </Box>

          <Typography sx={{ fontSize: 14, color: 'text.secondary', fontWeight: 500, lineHeight: 1.5 }}>
            {isViewOnly
              ? 'The deadline has passed. Your school photo selections are locked for printing.'
              : <>Tap the heart on a photo to add it to <Box component="strong" sx={{ fontWeight: 800 }}>{childName}</Box>’s book. Tap the heart again to remove it.</>}
          </Typography>
        </Box>

        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={handleFilterChange}
          size="small"
          sx={{
            bgcolor: 'grey.50',
            p: 0.5,
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'grey.200',
            flexWrap: 'wrap',
            '& .MuiToggleButton-root': {
              border: 'none',
              borderRadius: '6px !important',
              px: { xs: 1.2, sm: 1.7 },
              py: 0.75,
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.secondary',
              fontSize: 13,
              '&:hover': { bgcolor: 'grey.100' },
              '&.Mui-selected': {
                bgcolor: 'white',
                color: 'primary.main',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                '&:hover': { bgcolor: 'white' },
              },
            },
          }}
        >
          <ToggleButton value="all">
            <PhotoCameraIcon sx={{ mr: 0.75, fontSize: 18 }} /> All
          </ToggleButton>
          <ToggleButton value="selected">
            <FavoriteIcon sx={{ mr: 0.75, fontSize: 18 }} /> Selected ({likedCount})
          </ToggleButton>
          <ToggleButton value="solo">
            <PersonIcon sx={{ mr: 0.75, fontSize: 18 }} /> Solo
          </ToggleButton>
          <ToggleButton value="social">
            <GroupsIcon sx={{ mr: 0.75, fontSize: 18 }} /> Social
          </ToggleButton>
          <ToggleButton value="group">
            <GroupsIcon sx={{ mr: 0.75, fontSize: 18 }} /> Group
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {displayedMedia.length === 0 ? (
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 8, textAlign: 'center' }}>
          <FavoriteBorderIcon sx={{ fontSize: 42, color: 'grey.300', mb: 1 }} />
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'text.primary', mb: 0.5 }}>
            {filter === 'selected' ? 'No selected photos yet' : 'No photos in this view'}
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
            {filter === 'selected'
              ? 'Tap the heart on a school photo to include it in the book.'
              : 'Try a different photo filter.'}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            columnGap: { xs: 2, sm: 2.25, md: 2.5 },
            rowGap: { xs: 3.25, sm: 4, md: 4.5 },
            alignItems: 'start',
            justifyItems: 'stretch',
            justifyContent: 'start',
          }}
        >
          {displayedMedia.map((item, index) => {
            const selectionId = getSchoolPhotoSelectionId(item);
            const captionId = getBackendPhotoId(item);
            const caption = captionId ? captionsByPhotoId[captionId] || '' : '';
            const liked = photoMatchesIdSet(item, likedIds);
            return (
              <GalleryPhotoTile
                key={selectionId || captionId || index}
                item={item}
                caption={caption}
                isFetchingCaption={Boolean(captionId && fetchingCaptionIds[captionId])}
                isCover={selectionId === coverPhotoId}
                isSettingCover={selectionId === isSettingCoverId}
                isLiked={liked}
                isSelectionPending={Boolean(selectionId && updatingSelectionIds.has(selectionId))}
                isViewOnly={isViewOnly}
                atLimit={atLimit}
                onToggleLike={() => toggleLiked(item)}
                onZoom={() => handleOpenZoom(item)}
                onSetCover={() => handleSetCover(item)}
              />
            );
          })}
        </Box>
      )}

      <Dialog
        open={!!zoomItem}
        onClose={handleCloseZoom}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            borderRadius: { xs: 0, sm: '12px' },
            overflow: 'hidden',
            m: { xs: 0, sm: 2 },
            height: { xs: '100%', sm: 'auto' },
            maxHeight: '90vh',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: { xs: '100%', md: '80vh' } }}>
          <Box
            onClick={handleCloseZoom}
            sx={{
              flex: 1,
              bgcolor: '#0f172a',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              cursor: 'zoom-out',
              p: { xs: 0, sm: 2 },
              minHeight: { xs: '50vh', md: 'auto' },
            }}
          >
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                handleCloseZoom();
              }}
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.4)',
                backdropFilter: 'blur(4px)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                zIndex: 10,
              }}
            >
              <CloseIcon />
            </IconButton>
            {zoomItem && (
              <Box
                component="img"
                src={getSchoolPhotoUrl(zoomItem)}
                alt="School memory"
                sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            )}
          </Box>

          <Box sx={{ width: { xs: '100%', md: 380 }, bgcolor: 'background.paper', display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                p: 2.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 16 }}>Memory Details</Typography>
              {!isMobile && (
                <IconButton onClick={handleCloseZoom} size="small" sx={{ ml: 'auto' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            <Box sx={{ p: 3, flex: 1, overflowY: 'auto' }}>
              {zoomCaptionId && fetchingCaptionIds[zoomCaptionId] ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Loading caption…</Typography>
                </Box>
              ) : zoomCaption ? (
                <Typography sx={{ fontSize: 15, lineHeight: 1.6, color: 'text.primary', fontWeight: 500, whiteSpace: 'pre-wrap' }}>
                  {zoomCaption}
                </Typography>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: 14 }}>
                    No caption provided.
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider />
            <Box sx={{ p: 2, bgcolor: 'grey.50', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {!isViewOnly && (
                <Button
                  fullWidth
                  variant={zoomLiked ? 'outlined' : 'contained'}
                  color={zoomLiked ? 'error' : 'primary'}
                  disabled={zoomSelectionPending}
                  startIcon={
                    zoomSelectionPending ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : zoomLiked ? (
                      <FavoriteIcon />
                    ) : (
                      <FavoriteBorderIcon />
                    )
                  }
                  onClick={() => zoomItem && toggleLiked(zoomItem)}
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '6px' }}
                >
                  {zoomLiked ? 'Remove from Book' : atLimit ? 'Upgrade to Add More' : 'Add to Book'}
                </Button>
              )}

              <Button
                fullWidth
                variant={isZoomItemCover ? 'outlined' : 'contained'}
                color={isZoomItemCover ? 'inherit' : 'primary'}
                disabled={isZoomItemCover || !!isSettingCoverId}
                startIcon={
                  isSettingCoverId === zoomSelectionId ? (
                    <CircularProgress size={16} />
                  ) : (
                    <AutoAwesomeIcon />
                  )
                }
                onClick={() => zoomItem && handleSetCover(zoomItem)}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '6px' }}
              >
                {isZoomItemCover ? 'Current Cover Photo' : 'Set as Cover Photo'}
              </Button>

            </Box>
          </Box>
        </Box>
      </Dialog>

    </Box>
  );
}

function GalleryPhotoTile({
  item,
  caption,
  isCover,
  isSettingCover,
  isLiked,
  isSelectionPending,
  isViewOnly,
  atLimit,
  onToggleLike,
  onZoom,
  onSetCover,
}) {
  const url = getSchoolPhotoUrl(item);
  return (
    <Box sx={photoCardSx}>
      <Box
        sx={{
          ...photoFrameSx,
          borderColor: isLiked ? 'primary.main' : isCover ? 'primary.main' : 'grey.200',
          borderWidth: isLiked || isCover ? 2.5 : 1,
        }}
        onClick={onZoom}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onZoom();
          }
        }}
      >
        {url ? (
          <Box component="img" className="photo-image" src={url} alt={caption || 'School memory'} sx={photoImageSx} />
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'grey.100' }}>
            <CameraAltOutlinedIcon sx={{ fontSize: 34, color: 'grey.400' }} />
          </Box>
        )}

        {/* Visual-only hover layer. It never captures pointer events. */}
        <Box className="photo-hover-shade" sx={photoHoverShadeSx}>
          <ZoomInIcon sx={{ color: 'white', fontSize: 32, opacity: 0.9 }} />
        </Box>

        {!isViewOnly && (
          <Tooltip title={isLiked ? 'Unlike / remove from book' : atLimit ? 'Selection limit reached — tap to upgrade' : 'Like / add to book'}>
            <IconButton
              aria-label={isLiked ? 'Remove photo from book' : 'Like photo and add to book'}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onToggleLike();
              }}
              disabled={isSelectionPending}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 20,
                width: 36,
                height: 36,
                color: isLiked ? 'error.main' : 'text.secondary',
                bgcolor: 'rgba(255,255,255,0.96)',
                boxShadow: '0 2px 8px rgba(15,23,42,0.18)',
                pointerEvents: 'auto',
                '&:hover': { bgcolor: 'white', transform: 'scale(1.04)' },
              }}
            >
              {isSelectionPending ? (
                <CircularProgress size={18} color="inherit" />
              ) : isLiked ? (
                <FavoriteIcon sx={{ fontSize: 21 }} />
              ) : (
                <FavoriteBorderIcon sx={{ fontSize: 21 }} />
              )}
            </IconButton>
          </Tooltip>
        )}

        {isLiked && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              zIndex: 10,
              bgcolor: 'primary.main',
              color: 'white',
              px: 1.1,
              py: 0.35,
              borderRadius: '5px',
              fontSize: 10.5,
              fontWeight: 850,
              textTransform: 'uppercase',
              letterSpacing: '0.45px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.14)',
              pointerEvents: 'none',
            }}
          >
            In Book
          </Box>
        )}

        {!isLiked && isCover && (
          <Box sx={{ position: 'absolute', bottom: 8, left: 8, zIndex: 10, bgcolor: 'primary.main', color: 'white', px: 1.1, py: 0.35, borderRadius: '5px', fontSize: 10.5, fontWeight: 850, textTransform: 'uppercase', letterSpacing: '0.45px', pointerEvents: 'none' }}>
            Cover
          </Box>
        )}

        {!isViewOnly && !isCover && (
          <Button
            className="photo-cover-button"
            size="small"
            variant="contained"
            color="primary"
            disabled={isSettingCover}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onSetCover();
            }}
            sx={{
              position: 'absolute',
              bottom: 8,
              left: '50%',
              transform: 'translate(-50%, 4px)',
              zIndex: 12,
              opacity: 0,
              pointerEvents: 'auto',
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'none',
              py: 0.5,
              px: 2,
              borderRadius: '20px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
              whiteSpace: 'nowrap',
              transition: 'opacity 180ms ease, transform 180ms ease',
            }}
          >
            {isSettingCover ? <CircularProgress size={14} color="inherit" /> : 'Set as Cover'}
          </Button>
        )}


      </Box>

    </Box>
  );
}

const photoCardSx = {
  position: 'relative',
  width: '100%',
  minWidth: 0,
  bgcolor: 'transparent',
};

const photoFrameSx = {
  position: 'relative',
  aspectRatio: '4 / 3',
  overflow: 'hidden',
  bgcolor: '#f8fafc',
  borderRadius: 0,
  borderStyle: 'solid',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
  cursor: 'pointer',
  '&:hover .photo-image': { transform: 'scale(1.025)' },
  '&:hover .photo-hover-shade': { opacity: 1 },
  '&:hover .photo-cover-button': { opacity: 1, transform: 'translate(-50%, 0)' },
  '&:focus-within .photo-cover-button': { opacity: 1, transform: 'translate(-50%, 0)' },
};

const photoImageSx = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  display: 'block',
  borderRadius: 0,
  transform: 'scale(1)',
  transition: 'transform 320ms ease',
};

const photoHoverShadeSx = {
  position: 'absolute',
  inset: 0,
  zIndex: 2,
  opacity: 0,
  pointerEvents: 'none',
  transition: 'opacity 220ms ease',
  bgcolor: 'rgba(0,0,0,0.22)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};




// import React, { useEffect, useMemo, useState } from 'react';
// import {
//   Alert,
//   Box,
//   Button,
//   Checkbox,
//   CircularProgress,
//   Dialog,
//   DialogContent,
//   DialogTitle,
//   Divider,
//   IconButton,
//   TextField,
//   ToggleButton,
//   ToggleButtonGroup,
//   Tooltip,
//   Typography,
//   useMediaQuery,
//   useTheme,
// } from '@mui/material';

// import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
// import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
// import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
// import CloseIcon from '@mui/icons-material/Close';
// import CheckBoxIcon from '@mui/icons-material/CheckBox';
// import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
// import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
// import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
// import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
// import FavoriteIcon from '@mui/icons-material/Favorite';
// import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
// import GroupsIcon from '@mui/icons-material/Groups';
// import MenuBookIcon from '@mui/icons-material/MenuBook';
// import PersonIcon from '@mui/icons-material/Person';
// import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
// import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
// import ZoomInIcon from '@mui/icons-material/ZoomIn';

// import BookBuilder from '../components/BookBuilder';
// import { isDeadlinePassed } from '../components/SharedComponents';
// import useOrderMedia from '../hooks/useOrderMedia';
// import usePhotoCaptions from '../hooks/usePhotoCaptions';
// import useToast from '../hooks/useToast';
// import {
//   getBackendPhotoId,
//   getPhotoImageUrl,
//   isRealBackendPhotoId,
// } from '../hooks/useParentPhotos';
// import api from '../services/api';

// const CAPTION_MAX_CHARS = 80;

// function getSchoolPhotoSelectionId(item) {
//   return item?.media_item_id || null;
// }

// function getSchoolPhotoCandidateIds(item) {
//   return [
//     ...new Set(
//       [
//         item?.media_item_id,
//         item?.id,
//         item?.project_media_id,
//       ].filter(Boolean)
//     ),
//   ];
// }

// function photoMatchesIdSet(item, idSet) {
//   if (!idSet?.size) return false;
//   return getSchoolPhotoCandidateIds(item).some((id) => idSet.has(id));
// }

// function addPhotoAliases(targetSet, item) {
//   getSchoolPhotoCandidateIds(item).forEach((id) => targetSet.add(id));
// }

// function removePhotoAliases(targetSet, item) {
//   getSchoolPhotoCandidateIds(item).forEach((id) => targetSet.delete(id));
// }

// function getSchoolPhotoUrl(item) {
//   return item?.display_url || getPhotoImageUrl(item);
// }

// function isSchoolPhotoSelected(item) {
//   return Boolean(
//     item?.selected ??
//       item?.is_selected ??
//       item?.liked ??
//       item?.is_liked ??
//       item?.selected_for_book ??
//       item?.included_in_book
//   );
// }

// function collectSelectedIdsFromSchoolPhotos(data) {
//   const explicitSelectedIds = [
//     data?.selected_media_item_ids,
//     data?.selected_photo_ids,
//     data?.selected_ids,
//   ].find(Array.isArray);

//   if (explicitSelectedIds) {
//     return {
//       recognized: true,
//       ids: new Set(explicitSelectedIds.filter(Boolean)),
//     };
//   }

//   const hasChapterShape = Array.isArray(data) || Array.isArray(data?.chapters);
//   const hasDirectPhotoShape = Array.isArray(data?.photos) || Array.isArray(data?.items);

//   const chapters = Array.isArray(data)
//     ? data
//     : Array.isArray(data?.chapters)
//       ? data.chapters
//       : [];

//   const directPhotos = Array.isArray(data?.photos)
//     ? data.photos
//     : Array.isArray(data?.items)
//       ? data.items
//       : [];

//   const photos = [
//     ...directPhotos,
//     ...chapters.flatMap((chapter) => [
//       ...(Array.isArray(chapter?.photos) ? chapter.photos : []),
//       ...(Array.isArray(chapter?.media) ? chapter.media : []),
//     ]),
//   ];

//   return {
//     recognized: hasChapterShape || hasDirectPhotoShape,
//     ids: new Set(
//       photos
//         .filter(isSchoolPhotoSelected)
//         .flatMap(getSchoolPhotoCandidateIds)
//         .filter(Boolean)
//     ),
//   };
// }

// export default function MediaGallery({
//   orderId,
//   projectId,
//   limit = 0,
//   onUpgrade,
//   onAddSelectedToBook,
//   bookConfig,
//   childName = 'your child',
// }) {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
//   const toast = useToast();

//   const { mediaList, loading, error } = useOrderMedia(orderId);
//   const safeMediaList = useMemo(
//     () => (Array.isArray(mediaList) ? mediaList : []),
//     [mediaList]
//   );

//   const [filter, setFilter] = useState('all');
//   const [zoomItem, setZoomItem] = useState(null);
//   const [captionItem, setCaptionItem] = useState(null);
//   const [captionDraft, setCaptionDraft] = useState('');
//   const [isSavingCaption, setIsSavingCaption] = useState(false);
//   const [isDeletingCaption, setIsDeletingCaption] = useState(false);
//   const [showUpgradePopup, setShowUpgradePopup] = useState(false);
//   const [showPreviewModal, setShowPreviewModal] = useState(false);

//   // checkedIds = temporary multi-select (checkboxes) waiting for “Add to Book”.
//   // likedIds = persisted school-photo selections already saved on the project.
//   const [checkedIds, setCheckedIds] = useState(new Set());
//   const [likedIds, setLikedIds] = useState(new Set());
//   const [selectionLoading, setSelectionLoading] = useState(false);
//   const [updatingSelectionIds, setUpdatingSelectionIds] = useState(new Set());
//   const [isAddingSelected, setIsAddingSelected] = useState(false);

//   const [coverPhotoId, setCoverPhotoId] = useState(null);
//   const [isSettingCoverId, setIsSettingCoverId] = useState(null);

//   const isViewOnly = useMemo(
//     () => isDeadlinePassed(bookConfig?.content_deadline),
//     [bookConfig?.content_deadline]
//   );

//   const captionPhotos = useMemo(
//     () =>
//       safeMediaList.filter((item) =>
//         isRealBackendPhotoId(getBackendPhotoId(item))
//       ),
//     [safeMediaList]
//   );

//   const {
//     captionsByPhotoId,
//     fetchingCaptionIds,
//     saveCaption,
//     deleteCaption,
//   } = usePhotoCaptions(projectId, captionPhotos, {
//     fetchCaptions: false,
//     initialCaptionField: 'original_caption',
//   });

//   useEffect(() => {
//     if (!safeMediaList.length) {
//       setCoverPhotoId(null);
//       return;
//     }

//     const initialCover = safeMediaList.find((item) => item?.is_cover);
//     setCoverPhotoId(initialCover ? getSchoolPhotoSelectionId(initialCover) : null);
//   }, [safeMediaList]);

//   useEffect(() => {
//     // Reset project-scoped UI state only when the project/order changes.
//     // Do not reset likedIds when safeMediaList arrives later, otherwise a faster
//     // getSchoolPhotos response can be accidentally overwritten.
//     setCheckedIds(new Set());
//     setLikedIds(new Set());
//   }, [orderId, projectId]);

//   useEffect(() => {
//     const selectedFromMedia = new Set();
//     safeMediaList.filter(isSchoolPhotoSelected).forEach((item) => {
//       addPhotoAliases(selectedFromMedia, item);
//     });

//     if (selectedFromMedia.size > 0) {
//       setLikedIds((prev) => new Set([...prev, ...selectedFromMedia]));
//     }
//   }, [safeMediaList]);

//   useEffect(() => {
//     if (!projectId) return undefined;

//     let cancelled = false;

//     const loadSelections = async () => {
//       setSelectionLoading(true);
//       try {
//         const data = await api.getSchoolPhotos(projectId);
//         if (cancelled) return;

//         const persistedSelections = collectSelectedIdsFromSchoolPhotos(data);
//         if (persistedSelections.recognized) {
//           // Merge with flags already present on safeMediaList. This keeps the gallery
//           // source of truth as safeMediaList while still restoring saved likes.
//           setLikedIds((prev) => new Set([...prev, ...persistedSelections.ids]));
//         }
//       } catch (err) {
//         console.error('Failed to load school photo selections:', err);
//       } finally {
//         if (!cancelled) setSelectionLoading(false);
//       }
//     };

//     loadSelections();

//     return () => {
//       cancelled = true;
//     };
//   }, [projectId]);

//   const numericLimit = Number(limit);
//   const hasPhotoLimit = Number.isFinite(numericLimit) && numericLimit >= 0;
//   const photoLimit = hasPhotoLimit ? numericLimit : Infinity;

//   const likedMediaIds = useMemo(() => {
//     const ids = new Set();
//     safeMediaList.forEach((item) => {
//       if (photoMatchesIdSet(item, likedIds)) {
//         const id = getSchoolPhotoSelectionId(item);
//         if (id) ids.add(id);
//       }
//     });
//     return ids;
//   }, [safeMediaList, likedIds]);

//   const likedCount = likedMediaIds.size;
//   const checkedCount = checkedIds.size;
//   const atLimit = hasPhotoLimit && likedCount >= photoLimit;
//   const availableSlots = hasPhotoLimit
//     ? Math.max(photoLimit - likedCount, 0)
//     : Infinity;

//   // Always derive what is rendered from safeMediaList. Filters only choose which
//   // safeMediaList items are visible; they never replace the gallery data source.
//   const displayedMedia = useMemo(() => {
//     if (filter === 'all') return safeMediaList;

//     return safeMediaList.filter((item) => {
//       if (filter === 'selected') {
//         return photoMatchesIdSet(item, likedIds);
//       }

//       const faceCount = Number(item?.face_count) || 0;

//       let type = 'none';

//       if (faceCount === 1) {
//         type = 'solo';
//       } else if (faceCount >= 2 && faceCount <= 3) {
//         type = 'social';
//       } else if (faceCount >= 4) {
//         type = 'group';
//       }

//       return type === filter;
//     });
//   }, [filter, safeMediaList, likedIds]);

//   const handleFilterChange = (_event, newFilter) => {
//     if (newFilter !== null) setFilter(newFilter);
//   };

//   const openSelectionUpgrade = () => {
//     if (onUpgrade) {
//       onUpgrade({
//         source: 'school_photos',
//         reason: 'school_photo_limit',
//         remaining: availableSlots,
//         currentLimit: hasPhotoLimit ? photoLimit : null,
//         usedSlots: likedCount,
//       });
//     } else {
//       setShowUpgradePopup(true);
//     }
//   };

//   const setSelectionPending = (photoId, pending) => {
//     setUpdatingSelectionIds((prev) => {
//       const next = new Set(prev);
//       if (pending) next.add(photoId);
//       else next.delete(photoId);
//       return next;
//     });
//   };

//   const toggleChecked = (item) => {
//     if (isViewOnly) return;

//     const photoId = getSchoolPhotoSelectionId(item);
//     if (!photoId || photoMatchesIdSet(item, likedIds)) return;

//     const isChecked = checkedIds.has(photoId);
//     if (!isChecked && hasPhotoLimit && checkedCount >= availableSlots) {
//       openSelectionUpgrade();
//       return;
//     }

//     setCheckedIds((prev) => {
//       const next = new Set(prev);
//       if (next.has(photoId)) next.delete(photoId);
//       else next.add(photoId);
//       return next;
//     });
//   };

//   const handleAddSelectedToBook = async () => {
//     if (isViewOnly || !projectId || isAddingSelected || checkedIds.size === 0) return;

//     const idsToAdd = Array.from(checkedIds).filter(Boolean);
//     if (hasPhotoLimit && idsToAdd.length > availableSlots) {
//       openSelectionUpgrade();
//       return;
//     }

//     setIsAddingSelected(true);
//     try {
//       await api.updateSchoolPhotoSelections(projectId, {
//         select: idsToAdd,
//         deselect: [],
//       });

//       setLikedIds((prev) => {
//         const next = new Set(prev);
//         safeMediaList.forEach((item) => {
//           const canonicalId = getSchoolPhotoSelectionId(item);
//           if (canonicalId && checkedIds.has(canonicalId)) addPhotoAliases(next, item);
//         });
//         return next;
//       });
//       setCheckedIds(new Set());

//       if (onAddSelectedToBook) {
//         onAddSelectedToBook(idsToAdd);
//       }

//       toast.success(`${idsToAdd.length} photo${idsToAdd.length === 1 ? '' : 's'} added to the book.`);
//     } catch (err) {
//       console.error('Failed to add selected school photos:', err);
//       toast.error('Could not add the selected photos. Please try again.');
//     } finally {
//       setIsAddingSelected(false);
//     }
//   };

//   const toggleLiked = async (item, forceLiked) => {
//     if (isViewOnly || !projectId) return;

//     const photoId = getSchoolPhotoSelectionId(item);
//     if (!photoId || updatingSelectionIds.has(photoId)) return;

//     const wasLiked = photoMatchesIdSet(item, likedIds);
//     const nextLiked = typeof forceLiked === 'boolean' ? forceLiked : !wasLiked;
//     if (nextLiked === wasLiked) return;

//     if (nextLiked && atLimit) {
//       openSelectionUpgrade();
//       return;
//     }

//     setSelectionPending(photoId, true);
//     setLikedIds((prev) => {
//       const next = new Set(prev);
//       if (nextLiked) addPhotoAliases(next, item);
//       else removePhotoAliases(next, item);
//       return next;
//     });
//     if (nextLiked) {
//       setCheckedIds((prev) => {
//         const next = new Set(prev);
//         next.delete(photoId);
//         return next;
//       });
//     }

//     try {
//       await api.updateSchoolPhotoSelections(projectId, {
//         select: nextLiked ? [photoId] : [],
//         deselect: nextLiked ? [] : [photoId],
//       });

//       toast.success(nextLiked ? 'Photo added to the book.' : 'Photo removed from the book.', {
//         autoHideDuration: 1500,
//         replace: true,
//       });
//     } catch (err) {
//       console.error('Failed to update school photo selection:', err);
//       setLikedIds((prev) => {
//         const next = new Set(prev);
//         if (wasLiked) addPhotoAliases(next, item);
//         else removePhotoAliases(next, item);
//         return next;
//       });
//       toast.error('Could not update the photo selection. Please try again.');
//     } finally {
//       setSelectionPending(photoId, false);
//     }
//   };

//   const handleOpenZoom = (item) => setZoomItem(item);
//   const handleCloseZoom = () => setZoomItem(null);

//   const handleOpenCaption = (item) => {
//     if (isViewOnly) return;

//     const captionId = getBackendPhotoId(item);
//     if (!isRealBackendPhotoId(captionId)) return;

//     setCaptionItem(item);
//     setCaptionDraft(captionsByPhotoId[captionId] || '');
//   };

//   const handleCloseCaption = () => {
//     if (isSavingCaption || isDeletingCaption) return;
//     setCaptionItem(null);
//     setCaptionDraft('');
//   };

//   const closeCaptionAfterMutation = () => {
//     setCaptionItem(null);
//     setCaptionDraft('');
//   };

//   const handleSaveCaption = async () => {
//     if (!captionItem) return;

//     const captionId = getBackendPhotoId(captionItem);
//     if (!isRealBackendPhotoId(captionId)) return;

//     setIsSavingCaption(true);
//     try {
//       const result = await saveCaption(captionId, captionDraft);
//       if (result?.ok) {
//         toast.success(captionDraft.trim() ? 'Caption saved.' : 'Caption deleted.');
//         closeCaptionAfterMutation();
//       } else {
//         toast.error('Could not save caption. Please try again.');
//       }
//     } catch (err) {
//       console.error('Failed to save school photo caption:', err);
//       toast.error('Could not save caption. Please try again.');
//     } finally {
//       setIsSavingCaption(false);
//     }
//   };

//   const handleDeleteCaption = async () => {
//     if (!captionItem) return;

//     const captionId = getBackendPhotoId(captionItem);
//     if (!isRealBackendPhotoId(captionId)) return;

//     setIsDeletingCaption(true);
//     try {
//       const result = await deleteCaption(captionId);
//       if (result?.ok) {
//         toast.success('Caption deleted.');
//         closeCaptionAfterMutation();
//       } else {
//         toast.error('Could not delete caption. Please try again.');
//       }
//     } catch (err) {
//       console.error('Failed to delete school photo caption:', err);
//       toast.error('Could not delete caption. Please try again.');
//     } finally {
//       setIsDeletingCaption(false);
//     }
//   };

//   const handleSetCover = async (item) => {
//     const photoId = getSchoolPhotoSelectionId(item);
//     if (!photoId) return;

//     setIsSettingCoverId(photoId);
//     try {
//       // Existing gallery behavior retained until a school-photo cover endpoint is defined.
//       await new Promise((resolve) => setTimeout(resolve, 500));
//       setCoverPhotoId(photoId);
//     } catch (err) {
//       console.error('Failed to set cover:', err);
//     } finally {
//       setIsSettingCoverId(null);
//     }
//   };

//   const zoomSelectionId = zoomItem ? getSchoolPhotoSelectionId(zoomItem) : null;
//   const zoomCaptionId = zoomItem ? getBackendPhotoId(zoomItem) : null;
//   const zoomCaption = zoomCaptionId ? captionsByPhotoId[zoomCaptionId] || '' : '';
//   const zoomLiked = zoomItem ? photoMatchesIdSet(zoomItem, likedIds) : false;
//   const zoomSelectionPending = zoomSelectionId
//     ? updatingSelectionIds.has(zoomSelectionId)
//     : false;
//   const isZoomItemCover = zoomSelectionId && zoomSelectionId === coverPhotoId;

//   const captionModalId = captionItem ? getBackendPhotoId(captionItem) : null;
//   const captionModalExistingCaption = captionModalId
//     ? captionsByPhotoId[captionModalId] || ''
//     : '';
//   const captionModalPhotoUrl = captionItem ? getSchoolPhotoUrl(captionItem) : null;
//   const modalBusy = isSavingCaption || isDeletingCaption;
//   const captionTooLong = captionDraft.length > CAPTION_MAX_CHARS;

//   if (loading) {
//     return (
//       <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
//         <CircularProgress size={40} thickness={4} sx={{ color: 'primary.main' }} />
//       </Box>
//     );
//   }

//   if (error) {
//     return (
//       <Box p={3}>
//         <Alert severity="error" variant="filled" sx={{ borderRadius: '6px' }}>
//           {error}
//         </Alert>
//       </Box>
//     );
//   }

//   return (
//     <Box sx={{ pb: 6 }}>
//       <Box sx={{ mb: { xs: 4, md: 5 }, px: { xs: 2, sm: 3 } }}>
//         <Box
//           sx={{
//             display: 'flex',
//             flexDirection: { xs: 'column', sm: 'row' },
//             justifyContent: 'space-between',
//             alignItems: { xs: 'flex-start', sm: 'flex-end' },
//             gap: 3,
//             mb: 3,
//           }}
//         >
//           <Box>
//             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
//               <Typography sx={{ fontSize: { xs: 24, sm: 28 }, fontWeight: 850, color: 'text.primary' }}>
//                 School Photos Gallery
//               </Typography>

//               <Box
//                 sx={{
//                   display: 'flex',
//                   alignItems: 'center',
//                   gap: 1,
//                   bgcolor: 'primary.50',
//                   px: 1.25,
//                   py: 0.5,
//                   borderRadius: '6px',
//                   border: '1px solid',
//                   borderColor: 'primary.100',
//                 }}
//               >
//                 {selectionLoading && <CircularProgress size={13} />}
//                 <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'primary.900' }}>
//                   {likedCount} / {hasPhotoLimit ? photoLimit : '∞'} in book
//                 </Typography>
//                 {hasPhotoLimit && !isViewOnly && (
//                   <Button
//                     size="small"
//                     onClick={openSelectionUpgrade}
//                     sx={{
//                       minWidth: 0,
//                       p: 0,
//                       fontSize: 12,
//                       fontWeight: 800,
//                       textTransform: 'none',
//                       color: 'primary.main',
//                       '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
//                     }}
//                   >
//                     Upgrade
//                   </Button>
//                 )}
//               </Box>
//             </Box>

//             <Typography sx={{ fontSize: 14, color: 'text.secondary', fontWeight: 500 }}>
//               {isViewOnly
//                 ? 'The deadline has passed. Your school photo selections and captions are locked for printing.'
//                 : <>Check multiple photos and use <Box component="strong" sx={{ fontWeight: 800 }}>Add to Book</Box>, or tap the heart for one photo. Hearts show photos already saved in <Box component="strong" sx={{ fontWeight: 800 }}>{childName}</Box>’s book.</>}
//             </Typography>
//           </Box>

//           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
//             <Button
//               variant="outlined"
//               color="inherit"
//               startIcon={<MenuBookIcon />}
//               onClick={() => setShowPreviewModal(true)}
//               sx={{
//                 borderRadius: '8px',
//                 fontWeight: 600,
//                 px: 2,
//                 py: 0.75,
//                 borderColor: 'grey.300',
//                 color: 'text.primary',
//                 textTransform: 'none',
//                 '&:hover': { bgcolor: 'grey.100', borderColor: 'grey.400' },
//               }}
//             >
//               Preview Book
//             </Button>

//             {!isViewOnly && checkedCount > 0 && (
//               <Button
//                 variant="contained"
//                 color="primary"
//                 disableElevation
//                 disabled={isAddingSelected}
//                 onClick={handleAddSelectedToBook}
//                 startIcon={isAddingSelected ? <CircularProgress size={16} color="inherit" /> : undefined}
//                 sx={{ borderRadius: '8px', fontWeight: 700, px: 2.5, py: 0.8, textTransform: 'none' }}
//               >
//                 {isAddingSelected ? 'Adding…' : `Add ${checkedCount} to Book`}
//               </Button>
//             )}

//             <ToggleButtonGroup
//               value={filter}
//               exclusive
//               onChange={handleFilterChange}
//               size="small"
//               sx={{
//                 bgcolor: 'grey.50',
//                 p: 0.5,
//                 borderRadius: '8px',
//                 border: '1px solid',
//                 borderColor: 'grey.200',
//                 '& .MuiToggleButton-root': {
//                   border: 'none',
//                   borderRadius: '6px !important',
//                   px: { xs: 1.2, sm: 1.7 },
//                   py: 0.75,
//                   textTransform: 'none',
//                   fontWeight: 600,
//                   color: 'text.secondary',
//                   fontSize: 13,
//                   '&:hover': { bgcolor: 'grey.100' },
//                   '&.Mui-selected': {
//                     bgcolor: 'white',
//                     color: 'primary.main',
//                     boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
//                     '&:hover': { bgcolor: 'white' },
//                   },
//                 },
//               }}
//             >
//               <ToggleButton value="all">
//                 <PhotoCameraIcon sx={{ mr: 0.75, fontSize: 18 }} /> All
//               </ToggleButton>
//               <ToggleButton value="selected">
//                 <FavoriteIcon sx={{ mr: 0.75, fontSize: 18 }} /> Selected ({likedCount})
//               </ToggleButton>
//               <ToggleButton value="solo">
//                 <PersonIcon sx={{ mr: 0.75, fontSize: 18 }} /> Solo
//               </ToggleButton>

//               <ToggleButton value="social">
//                 <GroupsIcon sx={{ mr: 0.75, fontSize: 18 }} /> Social
//               </ToggleButton>

//               <ToggleButton value="group">
//                 <GroupsIcon sx={{ mr: 0.75, fontSize: 18 }} /> Group
//               </ToggleButton>
//             </ToggleButtonGroup>
//           </Box>
//         </Box>
//       </Box>

//       {displayedMedia.length === 0 ? (
//         <Box sx={{ px: { xs: 2, sm: 3 }, py: 8, textAlign: 'center' }}>
//           <FavoriteBorderIcon sx={{ fontSize: 42, color: 'grey.300', mb: 1 }} />
//           <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'text.primary', mb: 0.5 }}>
//             {filter === 'selected' ? 'No selected photos yet' : 'No photos in this view'}
//           </Typography>
//           <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
//             {filter === 'selected'
//               ? 'Tap the heart on a school photo to include it in the book.'
//               : 'Try a different photo filter.'}
//           </Typography>
//         </Box>
//       ) : (
//         <Box
//           sx={{
//             px: { xs: 2, sm: 3 },
//             display: 'grid',
//             gridTemplateColumns: {
//               xs: '1fr',
//               sm: 'repeat(2, 1fr)',
//               md: 'repeat(4, 1fr)',
//             },
//             columnGap: { xs: 2, sm: 2.25, md: 2.5 },
//             rowGap: { xs: 3.25, sm: 4, md: 4.5 },
//             alignItems: 'start',
//             justifyItems: 'stretch',
//             justifyContent: 'start',
//           }}
//         >
//           {displayedMedia.map((item, index) => {
//             const selectionId = getSchoolPhotoSelectionId(item);
//             const captionId = getBackendPhotoId(item);
//             const caption = captionId ? captionsByPhotoId[captionId] || '' : '';
//             const liked = photoMatchesIdSet(item, likedIds);
//             const checked = selectionId ? checkedIds.has(selectionId) : false;

//             return (
//               <GalleryPhotoTile
//                 key={selectionId || captionId || index}
//                 item={item}
//                 caption={caption}
//                 isFetchingCaption={Boolean(captionId && fetchingCaptionIds[captionId])}
//                 isCover={selectionId === coverPhotoId}
//                 isSettingCover={selectionId === isSettingCoverId}
//                 isChecked={checked}
//                 isLiked={liked}
//                 isSelectionPending={Boolean(selectionId && updatingSelectionIds.has(selectionId))}
//                 isViewOnly={isViewOnly}
//                 atLimit={atLimit}
//                 onToggleCheck={() => toggleChecked(item)}
//                 onToggleLike={() => toggleLiked(item)}
//                 onRemoveSelection={() => toggleLiked(item, false)}
//                 onZoom={() => handleOpenZoom(item)}
//                 onCaption={() => handleOpenCaption(item)}
//                 onSetCover={() => handleSetCover(item)}
//               />
//             );
//           })}
//         </Box>
//       )}

//       <Dialog
//         open={showPreviewModal}
//         onClose={() => setShowPreviewModal(false)}
//         maxWidth="md"
//         fullWidth
//         PaperProps={{ sx: { borderRadius: '12px', minHeight: '60vh' } }}
//       >
//         <DialogTitle
//           sx={{
//             display: 'flex',
//             justifyContent: 'space-between',
//             alignItems: 'center',
//             borderBottom: '1px solid',
//             borderColor: 'divider',
//             py: 2,
//           }}
//         >
//           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
//             <MenuBookIcon color="primary" />
//             <Typography sx={{ fontSize: 18, fontWeight: 800 }}>Book Preview</Typography>
//           </Box>
//           <IconButton onClick={() => setShowPreviewModal(false)} size="small">
//             <CloseIcon />
//           </IconButton>
//         </DialogTitle>
//         <Box sx={{ textAlign: 'center', width: '100%' }}>
//           <BookBuilder projectId={projectId} />
//         </Box>
//       </Dialog>

//       <Dialog
//         open={showUpgradePopup}
//         onClose={() => setShowUpgradePopup(false)}
//         maxWidth="xs"
//         fullWidth
//         PaperProps={{ sx: { borderRadius: '12px' } }}
//       >
//         <Box sx={{ p: 4, textAlign: 'center' }}>
//           <Box
//             sx={{
//               mx: 'auto',
//               width: 64,
//               height: 64,
//               bgcolor: 'primary.50',
//               color: 'primary.main',
//               borderRadius: '50%',
//               display: 'flex',
//               alignItems: 'center',
//               justifyContent: 'center',
//               mb: 2,
//             }}
//           >
//             <WorkspacePremiumIcon sx={{ fontSize: 36 }} />
//           </Box>
//           <Typography sx={{ fontSize: 22, fontWeight: 850, mb: 1, color: 'text.primary' }}>
//             Selection Limit Reached
//           </Typography>
//           <Typography sx={{ fontSize: 15, color: 'text.secondary', mb: 4, lineHeight: 1.5 }}>
//             You can currently select up to <strong>{hasPhotoLimit ? photoLimit : likedCount} school photos</strong> for the book.
//           </Typography>

//           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
//             <Button
//               variant="contained"
//               color="primary"
//               disableElevation
//               fullWidth
//               sx={{ py: 1.25, fontSize: 15, fontWeight: 700, borderRadius: '8px' }}
//               onClick={() => {
//                 if (onUpgrade) {
//                   onUpgrade({
//                     source: 'school_photos',
//                     reason: 'school_photo_limit',
//                     action: 'purchase',
//                     currentLimit: hasPhotoLimit ? photoLimit : null,
//                     usedSlots: likedCount,
//                   });
//                 }
//                 setShowUpgradePopup(false);
//               }}
//             >
//               See Upgrade Options
//             </Button>
//             <Button
//               variant="text"
//               color="inherit"
//               fullWidth
//               sx={{
//                 py: 1,
//                 fontSize: 14,
//                 fontWeight: 600,
//                 color: 'text.secondary',
//                 '&:hover': { bgcolor: 'grey.100' },
//               }}
//               onClick={() => setShowUpgradePopup(false)}
//             >
//               Maybe Later
//             </Button>
//           </Box>
//         </Box>
//       </Dialog>

//       <Dialog
//         open={!!zoomItem}
//         onClose={handleCloseZoom}
//         maxWidth="lg"
//         fullWidth
//         PaperProps={{
//           sx: {
//             bgcolor: 'background.paper',
//             borderRadius: { xs: 0, sm: '12px' },
//             overflow: 'hidden',
//             m: { xs: 0, sm: 2 },
//             height: { xs: '100%', sm: 'auto' },
//             maxHeight: '90vh',
//           },
//         }}
//       >
//         <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: { xs: '100%', md: '80vh' } }}>
//           <Box
//             onClick={handleCloseZoom}
//             sx={{
//               flex: 1,
//               bgcolor: '#0f172a',
//               display: 'flex',
//               justifyContent: 'center',
//               alignItems: 'center',
//               position: 'relative',
//               cursor: 'zoom-out',
//               p: { xs: 0, sm: 2 },
//               minHeight: { xs: '50vh', md: 'auto' },
//             }}
//           >
//             <IconButton
//               onClick={(event) => {
//                 event.stopPropagation();
//                 handleCloseZoom();
//               }}
//               sx={{
//                 position: 'absolute',
//                 top: 12,
//                 left: 12,
//                 color: 'white',
//                 bgcolor: 'rgba(0,0,0,0.4)',
//                 backdropFilter: 'blur(4px)',
//                 '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
//                 zIndex: 10,
//               }}
//             >
//               <CloseIcon />
//             </IconButton>
//             {zoomItem && (
//               <Box
//                 component="img"
//                 src={getSchoolPhotoUrl(zoomItem)}
//                 alt="School memory"
//                 sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
//               />
//             )}
//           </Box>

//           <Box sx={{ width: { xs: '100%', md: 380 }, bgcolor: 'background.paper', display: 'flex', flexDirection: 'column' }}>
//             <Box
//               sx={{
//                 p: 2.5,
//                 borderBottom: '1px solid',
//                 borderColor: 'divider',
//                 display: 'flex',
//                 alignItems: 'center',
//                 justifyContent: 'space-between',
//               }}
//             >
//               <Typography sx={{ fontWeight: 800, fontSize: 16 }}>Memory Details</Typography>
//               {!isMobile && (
//                 <IconButton onClick={handleCloseZoom} size="small" sx={{ ml: 'auto' }}>
//                   <CloseIcon fontSize="small" />
//                 </IconButton>
//               )}
//             </Box>

//             <Box sx={{ p: 3, flex: 1, overflowY: 'auto' }}>
//               {zoomCaptionId && fetchingCaptionIds[zoomCaptionId] ? (
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                   <CircularProgress size={16} />
//                   <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Loading caption…</Typography>
//                 </Box>
//               ) : zoomCaption ? (
//                 <Box sx={{ display: 'flex', gap: 1.5 }}>
//                   <FormatQuoteIcon sx={{ color: 'primary.light', transform: 'rotate(180deg)' }} />
//                   <Typography sx={{ fontSize: 15, lineHeight: 1.6, color: 'text.primary', fontWeight: 500, whiteSpace: 'pre-wrap' }}>
//                     {zoomCaption}
//                   </Typography>
//                 </Box>
//               ) : (
//                 <Box sx={{ textAlign: 'center', py: 4 }}>
//                   <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: 14 }}>
//                     No caption provided.
//                   </Typography>
//                 </Box>
//               )}
//             </Box>

//             <Divider />
//             <Box sx={{ p: 2, bgcolor: 'grey.50', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
//               {!isViewOnly && (
//                 <Button
//                   fullWidth
//                   variant={zoomLiked ? 'outlined' : 'contained'}
//                   color={zoomLiked ? 'error' : 'primary'}
//                   disabled={zoomSelectionPending}
//                   startIcon={
//                     zoomSelectionPending ? (
//                       <CircularProgress size={16} color="inherit" />
//                     ) : zoomLiked ? (
//                       <DeleteOutlineIcon />
//                     ) : (
//                       <FavoriteBorderIcon />
//                     )
//                   }
//                   onClick={() => zoomItem && toggleLiked(zoomItem)}
//                   sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '6px' }}
//                 >
//                   {zoomLiked ? 'Remove from Book' : atLimit ? 'Upgrade to Add More' : 'Add to Book'}
//                 </Button>
//               )}

//               <Button
//                 fullWidth
//                 variant={isZoomItemCover ? 'outlined' : 'contained'}
//                 color={isZoomItemCover ? 'inherit' : 'primary'}
//                 disabled={isZoomItemCover || !!isSettingCoverId}
//                 startIcon={
//                   isSettingCoverId === zoomSelectionId ? (
//                     <CircularProgress size={16} />
//                   ) : (
//                     <AutoAwesomeIcon />
//                   )
//                 }
//                 onClick={() => zoomItem && handleSetCover(zoomItem)}
//                 sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '6px' }}
//               >
//                 {isZoomItemCover ? 'Current Cover Photo' : 'Set as Cover Photo'}
//               </Button>

//               {!isViewOnly && (
//                 <Button
//                   fullWidth
//                   startIcon={zoomCaption ? <EditOutlinedIcon /> : <AddCommentOutlinedIcon />}
//                   onClick={() => {
//                     const item = zoomItem;
//                     handleCloseZoom();
//                     if (item) handleOpenCaption(item);
//                   }}
//                   sx={{
//                     textTransform: 'none',
//                     fontWeight: 600,
//                     color: 'text.secondary',
//                     bgcolor: 'white',
//                     border: '1px solid',
//                     borderColor: 'grey.300',
//                     '&:hover': { bgcolor: 'grey.100' },
//                   }}
//                 >
//                   {zoomCaption ? 'Edit Caption' : 'Add a Caption'}
//                 </Button>
//               )}
//             </Box>
//           </Box>
//         </Box>
//       </Dialog>

//       <Dialog
//         open={!!captionItem}
//         onClose={handleCloseCaption}
//         fullWidth
//         fullScreen={isMobile}
//         maxWidth={false}
//         PaperProps={{
//           sx: {
//             width: { xs: '100%', sm: 'min(1060px, calc(100vw - 64px))' },
//             height: { xs: '100%', sm: 'min(760px, calc(100vh - 64px))' },
//             borderRadius: '6px',
//             overflow: 'hidden',
//           },
//         }}
//       >
//         <DialogTitle
//           sx={{
//             py: 1.75,
//             px: { xs: 2, sm: 3 },
//             borderBottom: '1px solid',
//             borderColor: 'divider',
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'space-between',
//             gap: 2,
//           }}
//         >
//           <Typography sx={{ fontSize: 20, fontWeight: 850 }}>
//             {captionModalExistingCaption ? 'Edit caption' : 'Add caption'}
//           </Typography>
//           <IconButton aria-label="Close caption editor" onClick={handleCloseCaption} disabled={modalBusy}>
//             <CloseIcon />
//           </IconButton>
//         </DialogTitle>

//         <DialogContent
//           sx={{
//             p: 0,
//             display: 'flex',
//             flexDirection: { xs: 'column', sm: 'row' },
//             minHeight: 0,
//           }}
//         >
//           <Box
//             sx={{
//               flex: { xs: '0 0 46%', sm: '1 1 auto' },
//               minWidth: 0,
//               bgcolor: 'grey.50',
//               display: 'flex',
//               alignItems: 'center',
//               justifyContent: 'center',
//               p: { xs: 1.5, sm: 2 },
//             }}
//           >
//             {captionModalPhotoUrl ? (
//               <Box
//                 component="img"
//                 src={captionModalPhotoUrl}
//                 alt="Selected school memory"
//                 sx={{
//                   width: '100%',
//                   height: '100%',
//                   maxWidth: '100%',
//                   maxHeight: '100%',
//                   objectFit: 'contain',
//                   borderRadius: 0,
//                 }}
//               />
//             ) : (
//               <Box sx={{ textAlign: 'center', color: 'grey.400' }}>
//                 <CameraAltOutlinedIcon sx={{ fontSize: 40, mb: 1 }} />
//                 <Typography sx={{ fontSize: 13 }}>Photo preview unavailable</Typography>
//               </Box>
//             )}
//           </Box>

//           <Box
//             sx={{
//               flex: { xs: '1 1 auto', sm: '0 0 380px' },
//               p: { xs: 2, sm: 3 },
//               display: 'flex',
//               flexDirection: 'column',
//               gap: 2,
//               bgcolor: 'background.default',
//               minHeight: 0,
//             }}
//           >
//             <Box sx={{ minHeight: 0 }}>
//               {captionModalExistingCaption && (
//                 <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
//                   <Button
//                     onClick={handleDeleteCaption}
//                     disabled={modalBusy}
//                     color="error"
//                     size="small"
//                     startIcon={
//                       isDeletingCaption ? (
//                         <CircularProgress size={14} color="inherit" />
//                       ) : (
//                         <DeleteOutlineIcon sx={{ fontSize: 17 }} />
//                       )
//                     }
//                     sx={{ px: 0, minWidth: 'auto', fontWeight: 800, '&:hover': { bgcolor: 'transparent' } }}
//                   >
//                     Delete caption
//                   </Button>
//                 </Box>
//               )}

//               <TextField
//                 autoFocus={!isMobile}
//                 fullWidth
//                 multiline
//                 minRows={isMobile ? 6 : 14}
//                 maxRows={isMobile ? 8 : 18}
//                 variant="outlined"
//                 placeholder={`Write a short caption about this school memory for ${childName}.`}
//                 value={captionDraft}
//                 onChange={(event) => {
//                   const nextValue = event.target.value || '';
//                   setCaptionDraft(nextValue.slice(0, CAPTION_MAX_CHARS));
//                 }}
//                 disabled={modalBusy}
//                 error={captionTooLong}
//                 inputProps={{ maxLength: CAPTION_MAX_CHARS }}
//                 helperText={`${captionDraft.length}/${CAPTION_MAX_CHARS}`}
//                 FormHelperTextProps={{
//                   sx: {
//                     mx: 0,
//                     textAlign: 'right',
//                     color: captionTooLong ? 'error.main' : 'text.secondary',
//                     fontSize: 12,
//                     fontWeight: 750,
//                   },
//                 }}
//                 sx={softCaptionTextareaSx}
//               />
//             </Box>

//             <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
//               <Button onClick={handleCloseCaption} disabled={modalBusy} color="inherit">
//                 Cancel
//               </Button>
//               <Button
//                 onClick={handleSaveCaption}
//                 variant="contained"
//                 disableElevation
//                 disabled={modalBusy || captionTooLong}
//                 sx={{ borderRadius: '6px' }}
//               >
//                 {isSavingCaption ? <CircularProgress size={20} color="inherit" /> : 'Save'}
//               </Button>
//             </Box>
//           </Box>
//         </DialogContent>
//       </Dialog>
//     </Box>
//   );
// }

// function GalleryPhotoTile({
//   item,
//   caption,
//   isFetchingCaption,
//   isCover,
//   isSettingCover,
//   isChecked,
//   isLiked,
//   isSelectionPending,
//   isViewOnly,
//   atLimit,
//   onToggleCheck,
//   onToggleLike,
//   onRemoveSelection,
//   onZoom,
//   onCaption,
//   onSetCover,
// }) {
//   const url = getSchoolPhotoUrl(item);
//   const hasCaption = Boolean(caption?.trim());
//   const showCaptionFooter = !isViewOnly || hasCaption || isFetchingCaption;

//   return (
//     <Box sx={photoCardSx}>
//       <Box
//         sx={{
//           ...photoFrameSx,
//           borderColor: isLiked ? 'primary.main' : isChecked ? 'primary.light' : isCover ? 'primary.main' : 'grey.200',
//           borderWidth: isLiked || isChecked || isCover ? 2.5 : 1,
//         }}
//         onClick={onZoom}
//         role="button"
//         tabIndex={0}
//         onKeyDown={(event) => {
//           if (event.key === 'Enter' || event.key === ' ') {
//             event.preventDefault();
//             onZoom();
//           }
//         }}
//       >
//         {url ? (
//           <Box component="img" className="photo-image" src={url} alt={caption || 'School memory'} sx={photoImageSx} />
//         ) : (
//           <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'grey.100' }}>
//             <CameraAltOutlinedIcon sx={{ fontSize: 34, color: 'grey.400' }} />
//           </Box>
//         )}

//         {/* Visual-only hover layer. It never captures pointer events, so checkbox,
//             heart, delete and cover actions remain tappable above it. */}
//         <Box className="photo-hover-shade" sx={photoHoverShadeSx}>
//           <ZoomInIcon sx={{ color: 'white', fontSize: 32, opacity: 0.9 }} />
//         </Box>

//         {!isViewOnly && (
//           <Tooltip title={isLiked ? 'Already in book' : isChecked ? 'Remove from multi-select' : 'Select for Add to Book'}>
//             <span style={{ position: 'absolute', top: 8, left: 8, zIndex: 20 }}>
//               <Checkbox
//                 checked={isChecked}
//                 disabled={isLiked}
//                 onClick={(event) => event.stopPropagation()}
//                 onChange={(event) => {
//                   event.stopPropagation();
//                   onToggleCheck();
//                 }}
//                 icon={<CheckBoxOutlineBlankIcon />}
//                 checkedIcon={<CheckBoxIcon />}
//                 size="small"
//                 sx={{
//                   p: 0.65,
//                   borderRadius: '6px',
//                   color: 'grey.700',
//                   bgcolor: 'rgba(255,255,255,0.94)',
//                   boxShadow: '0 2px 8px rgba(15,23,42,0.14)',
//                   '&.Mui-checked': { color: 'primary.main' },
//                   '&:hover': { bgcolor: 'white' },
//                   '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.82)', color: 'grey.400' },
//                 }}
//               />
//             </span>
//           </Tooltip>
//         )}

//         {!isViewOnly && (
//           <Tooltip title={isLiked ? 'Unlike / remove from book' : atLimit ? 'Selection limit reached — tap to upgrade' : 'Like / add to book'}>
//             <IconButton
//               aria-label={isLiked ? 'Remove photo from book' : 'Like photo and add to book'}
//               onClick={(event) => {
//                 event.preventDefault();
//                 event.stopPropagation();
//                 onToggleLike();
//               }}
//               disabled={isSelectionPending}
//               size="small"
//               sx={{
//                 position: 'absolute',
//                 top: 8,
//                 right: 8,
//                 zIndex: 20,
//                 width: 36,
//                 height: 36,
//                 color: isLiked ? 'error.main' : 'text.secondary',
//                 bgcolor: 'rgba(255,255,255,0.96)',
//                 boxShadow: '0 2px 8px rgba(15,23,42,0.18)',
//                 pointerEvents: 'auto',
//                 '&:hover': { bgcolor: 'white', transform: 'scale(1.04)' },
//               }}
//             >
//               {isSelectionPending ? (
//                 <CircularProgress size={18} color="inherit" />
//               ) : isLiked ? (
//                 <FavoriteIcon sx={{ fontSize: 21 }} />
//               ) : (
//                 <FavoriteBorderIcon sx={{ fontSize: 21 }} />
//               )}
//             </IconButton>
//           </Tooltip>
//         )}

//         {isLiked && (
//           <Box
//             sx={{
//               position: 'absolute',
//               bottom: 8,
//               left: 8,
//               zIndex: 10,
//               bgcolor: 'primary.main',
//               color: 'white',
//               px: 1.1,
//               py: 0.35,
//               borderRadius: '5px',
//               fontSize: 10.5,
//               fontWeight: 850,
//               textTransform: 'uppercase',
//               letterSpacing: '0.45px',
//               boxShadow: '0 2px 6px rgba(0,0,0,0.14)',
//               pointerEvents: 'none',
//             }}
//           >
//             In Book
//           </Box>
//         )}

//         {!isLiked && isCover && (
//           <Box sx={{ position: 'absolute', bottom: 8, left: 8, zIndex: 10, bgcolor: 'primary.main', color: 'white', px: 1.1, py: 0.35, borderRadius: '5px', fontSize: 10.5, fontWeight: 850, textTransform: 'uppercase', letterSpacing: '0.45px', pointerEvents: 'none' }}>
//             Cover
//           </Box>
//         )}

//         {!isViewOnly && !isCover && (
//           <Button
//             className="photo-cover-button"
//             size="small"
//             variant="contained"
//             color="primary"
//             disabled={isSettingCover}
//             onClick={(event) => {
//               event.preventDefault();
//               event.stopPropagation();
//               onSetCover();
//             }}
//             sx={{
//               position: 'absolute',
//               bottom: 8,
//               left: '50%',
//               transform: 'translate(-50%, 4px)',
//               zIndex: 12,
//               opacity: 0,
//               pointerEvents: 'auto',
//               fontSize: 11,
//               fontWeight: 700,
//               textTransform: 'none',
//               py: 0.5,
//               px: 2,
//               borderRadius: '20px',
//               boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
//               whiteSpace: 'nowrap',
//               transition: 'opacity 180ms ease, transform 180ms ease',
//             }}
//           >
//             {isSettingCover ? <CircularProgress size={14} color="inherit" /> : 'Set as Cover'}
//           </Button>
//         )}

//         {isLiked && !isViewOnly && (
//           <Tooltip title="Delete from book selection">
//             <IconButton
//               aria-label="Delete photo from book selection"
//               className="photo-remove-button"
//               onClick={(event) => {
//                 event.preventDefault();
//                 event.stopPropagation();
//                 onRemoveSelection();
//               }}
//               disabled={isSelectionPending}
//               size="small"
//               sx={floatingIconButtonSx({ bottom: 8, right: 8 })}
//             >
//               <DeleteOutlineIcon sx={{ fontSize: 18 }} />
//             </IconButton>
//           </Tooltip>
//         )}
//       </Box>

//       {showCaptionFooter && (
//         <Box sx={captionFooterSx}>
//           {isFetchingCaption ? (
//             <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minHeight: 45 }}>
//               <CircularProgress size={15} />
//               <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 750 }}>
//                 Loading caption
//               </Typography>
//             </Box>
//           ) : isViewOnly ? (
//             <Typography sx={captionTextSx}>{caption}</Typography>
//           ) : (
//             <Box component="button" type="button" onClick={onCaption} sx={captionButtonSx}>
//               <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
//                 <Typography sx={hasCaption ? captionTextSx : emptyCaptionTextSx}>
//                   {hasCaption ? caption : 'No caption yet'}
//                 </Typography>
//               </Box>
//               <Box className="caption-icon-wrap" sx={captionIconWrapSx}>
//                 {hasCaption ? <EditOutlinedIcon sx={captionIconSx} /> : <AddCommentOutlinedIcon sx={captionIconSx} />}
//               </Box>
//             </Box>
//           )}
//         </Box>
//       )}
//     </Box>
//   );
// }

// const photoCardSx = {
//   position: 'relative',
//   width: '100%',
//   minWidth: 0,
//   bgcolor: 'transparent',
// };

// const photoFrameSx = {
//   position: 'relative',
//   aspectRatio: '4 / 3',
//   overflow: 'hidden',
//   bgcolor: '#f8fafc',
//   borderRadius: 0,
//   borderStyle: 'solid',
//   boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
//   cursor: 'pointer',
//   '&:hover .photo-image': { transform: 'scale(1.025)' },
//   '&:hover .photo-hover-shade': { opacity: 1 },
//   '&:hover .photo-remove-button': { opacity: 1, transform: 'translateY(0)' },
//   '&:focus-within .photo-remove-button': { opacity: 1, transform: 'translateY(0)' },
//   '&:hover .photo-cover-button': { opacity: 1, transform: 'translate(-50%, 0)' },
//   '&:focus-within .photo-cover-button': { opacity: 1, transform: 'translate(-50%, 0)' },
// };

// const photoImageSx = {
//   width: '100%',
//   height: '100%',
//   objectFit: 'contain',
//   display: 'block',
//   borderRadius: 0,
//   transform: 'scale(1)',
//   transition: 'transform 320ms ease',
// };

// const photoHoverShadeSx = {
//   position: 'absolute',
//   inset: 0,
//   zIndex: 2,
//   opacity: 0,
//   pointerEvents: 'none',
//   transition: 'opacity 220ms ease',
//   bgcolor: 'rgba(0,0,0,0.22)',
//   display: 'flex',
//   justifyContent: 'center',
//   alignItems: 'center',
// };

// const captionFooterSx = { pt: 1, minHeight: 42 };

// const captionButtonSx = {
//   width: '100%',
//   minHeight: 36,
//   px: 0,
//   py: 0.6,
//   border: 0,
//   borderRadius: '6px',
//   bgcolor: 'transparent',
//   color: 'text.primary',
//   cursor: 'pointer',
//   display: 'flex',
//   alignItems: 'center',
//   justifyContent: 'space-between',
//   gap: 1,
//   textAlign: 'left',
//   font: 'inherit',
//   transition: 'color 160ms ease',
//   '&:hover': { color: 'primary.main' },
//   '&:hover .caption-icon-wrap': { bgcolor: 'grey.200' },
//   '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 3 },
// };

// const captionTextSx = {
//   fontSize: 13.5,
//   lineHeight: 1.4,
//   color: 'text.secondary',
//   fontWeight: 600,
//   display: '-webkit-box',
//   WebkitLineClamp: 2,
//   WebkitBoxOrient: 'vertical',
//   overflow: 'hidden',
//   pt: 0.5,
// };

// const emptyCaptionTextSx = {
//   ...captionTextSx,
//   color: 'grey.400',
//   fontWeight: 500,
//   fontStyle: 'italic',
// };

// const captionIconWrapSx = {
//   width: 32,
//   height: 32,
//   borderRadius: '6px',
//   flex: '0 0 auto',
//   bgcolor: 'grey.100',
//   color: 'text.secondary',
//   display: 'flex',
//   alignItems: 'center',
//   justifyContent: 'center',
//   transition: 'background-color 160ms ease',
// };

// const captionIconSx = { fontSize: 18, color: 'inherit' };

// const floatingIconButtonSx = ({ top, right, bottom, left }) => ({
//   position: 'absolute',
//   ...(top !== undefined ? { top } : {}),
//   ...(right !== undefined ? { right } : {}),
//   ...(bottom !== undefined ? { bottom } : {}),
//   ...(left !== undefined ? { left } : {}),
//   zIndex: 6,
//   width: 32,
//   height: 32,
//   bgcolor: 'rgba(255,255,255,0.94)',
//   color: 'error.main',
//   boxShadow: '0 2px 8px rgba(15,23,42,0.14)',
//   opacity: { xs: 1, md: 0 },
//   transform: { xs: 'none', md: 'translateY(-3px)' },
//   transition: 'opacity 160ms ease, transform 160ms ease, background-color 160ms ease',
//   '&:hover': { bgcolor: 'white' },
// });

// const softCaptionTextareaSx = {
//   '& .MuiOutlinedInput-root': {
//     borderRadius: '6px',
//     bgcolor: 'grey.50',
//     alignItems: 'flex-start',
//     '& fieldset': { borderColor: 'grey.300' },
//     '&:hover fieldset': { borderColor: 'grey.400' },
//   },
//   '& textarea': { fontSize: 15, lineHeight: 1.5 },
// };
